import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/stores/auth.store';
import { AuthService } from '@/lib/api/auth.service';
import { resetUnauthorizedLogoutGuard, setLoggingOut } from '@/lib/api/client';
import { Session, useSessionContext, buildAuthRedirectToUrl, replaceWindowLocation } from '@om/shared-auth';

export const useAuth = () => {
  const sessionContext = useSessionContext();
  const lastResolvedSessionUserIdRef = useRef<string | null>(null);
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    setUser,
    setAuthenticated,
    clearAuth,
    setLoading,
    setError,
  } = useAuthStore();

  const doesSessionExist = !sessionContext.loading && sessionContext.doesSessionExist;

  useEffect(() => {
    let active = true;

    const syncAuthState = async () => {
      if (sessionContext.loading) {
        setLoading(true);
        return;
      }

      setLoading(false);

      if (!doesSessionExist) {
        if (!active) return;
        lastResolvedSessionUserIdRef.current = null;
        setAuthenticated(false);
        setUser(null);
        return;
      }

      let isGuestSession = false;
      let hasValidAccessTokenPayload = false;
      try {
        const payload = await Session.getAccessTokenPayloadSecurely();
        hasValidAccessTokenPayload = true;
        isGuestSession = payload?.isGuest === true;
      } catch {
        isGuestSession = false;
        hasValidAccessTokenPayload = false;
      }

      if (!active) return;

      if (!hasValidAccessTokenPayload) {
        lastResolvedSessionUserIdRef.current = null;
        setAuthenticated(false);
        setUser(null);
        setError('Your session is invalid or expired. Please sign in again.');
        return;
      }

      if (isGuestSession) {
        setAuthenticated(false);
        setUser(null);
        setError(null);
        return;
      }

      setAuthenticated(true);
      resetUnauthorizedLogoutGuard();

      const sessionUserId = sessionContext.userId ?? null;
      const didSessionUserChange =
        Boolean(sessionUserId)
        && lastResolvedSessionUserIdRef.current !== null
        && lastResolvedSessionUserIdRef.current !== sessionUserId;

      if (didSessionUserChange) {
        // Prevent stale identity rendering when switching accounts without a full reload.
        setUser(null);
      }

      if (!user || didSessionUserChange) {
        AuthService.getProfile()
          .then((response) => {
            if (!active) return;
            setUser(response.user);
            lastResolvedSessionUserIdRef.current = sessionUserId;
          })
          .catch((profileError) => {
            if (!active) return;
            const message = profileError instanceof Error ? profileError.message : 'Failed to load profile';
            setError(message);
          });
      } else if (sessionUserId) {
        lastResolvedSessionUserIdRef.current = sessionUserId;
      }
    };

    void syncAuthState();

    return () => {
      active = false;
    };
  }, [sessionContext.loading, doesSessionExist, user, setLoading, setAuthenticated, setUser, setError]);

  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Set logging out flag to prevent new requests and cancel pending ones
      setLoggingOut(true);
      
      // Clear auth state immediately
      clearAuth();

      // Sign out via SuperTokens
      await Session.signOut();
      
    } catch (e) {
      // Ignore any errors during logout process
      console.warn('Error during logout process:', e);
    } finally {
      // Always complete these steps
      setLoggingOut(false);
      setLoading(false);
      replaceWindowLocation(buildAuthRedirectToUrl(window.location.href));
    }
  }, [clearAuth, setLoading, setError]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  return {
    user,
    isAuthenticated,
    isLoading: sessionContext.loading || isLoading,
    error,
    logout,
    clearError,
  };
};
